import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { catchError, finalize, forkJoin, of, switchMap } from 'rxjs';
import { Building, FloorArea, ImprovementsPayload, Job, OtherBuilding, PropertyInfo, RuralInfo, ValuationTypeOption, emptyBuilding, emptyOtherBuilding } from '../../model/tops.models';
import { cleanFormValue, coerceNumber, joinCsv, splitCsv } from '../../mixins/form-utils';
import { formatDisplayDate, parseAppointment } from '../../mixins/date-utils';
import { ApiService, JobSyncConflict, JobSyncContentMode, JobSyncDraft } from '../../service/api.service';
import { CameraChannel, CameraChannelService } from '../../service/camera-channel.service';
import { ConnectivityService } from '../../service/connectivity.service';
import { OptionListName, OptionsService } from '../../service/options.service';
import { ToastService } from '../../service/toast.service';
import { UnsavedChangesService } from '../../service/unsaved-changes.service';
import { CameraPopoutComponent } from '../camera-popout/camera-popout.component';
import { JobFormModalComponent } from '../shared/job-form-modal.component';
import { OptionGroup, OptionItem, OptionModalComponent } from '../shared/option-modal.component';

type TabId = 'general' | 'improvements' | 'notes';
type FieldKind = 'text' | 'textarea' | 'number' | 'option' | 'year';
type BuildingFieldKind = 'text' | 'textarea' | 'number' | 'option';
type BuildingFieldKey = keyof Building & string;
type OtherBuildingFieldKey = keyof OtherBuilding & string;

interface FieldConfig {
  key: string;
  label: string;
  kind: FieldKind;
  optionList?: OptionListName;
  singleSelect?: boolean;
}

interface BuildingFieldConfig {
  key: BuildingFieldKey;
  label: string;
  kind: BuildingFieldKind;
  optionList?: OptionListName;
  singleSelect?: boolean;
}

interface QuickSelectGroup {
  key: BuildingFieldKey;
  label: string;
  options: string[];
  selected: string[];
  singleSelect: boolean;
}

interface CalendarDay {
  date: Date;
  day: number;
  currentMonth: boolean;
}

@Component({
  selector: 'app-job-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, CameraPopoutComponent, JobFormModalComponent, OptionModalComponent],
  templateUrl: './job-detail.component.html',
  styleUrl: './job-detail.component.css',
})
export class JobDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly options = inject(OptionsService);
  private readonly unsaved = inject(UnsavedChangesService);
  private readonly cameraChannels = inject(CameraChannelService);

  private drawingCanvas?: ElementRef<HTMLCanvasElement>;

  @ViewChild('drawingCanvas') set drawingCanvasElement(canvas: ElementRef<HTMLCanvasElement> | undefined) {
    this.drawingCanvas = canvas;
    if (canvas) this.restoreDrawingAfterRender();
  }

  jobId = 0;
  job: Job | null = null;
  loading = true;
  saving = false;
  syncing = false;
  isOnline = this.connectivity.currentOnline;
  syncDraft: JobSyncDraft | null = null;
  syncConflicts: JobSyncConflict[] = [];
  syncReviewOpen = false;
  syncChoiceOpen = false;
  syncContentMode: JobSyncContentMode = 'fields-and-photos';
  syncPhotoCount = 0;
  syncSuccessOpen = false;
  unsavedBackPromptOpen = false;
  deleteDwellingPrompt = { open: false, buildingId: '', buildingName: '' };
  fetchingRemoteJob = false;
  activeTab: TabId = 'general';
  isCopyMode = false;
  showEditJobModal = false;
  optionModal = { open: false, title: '', field: '', options: [] as string[], optionGroups: [] as OptionGroup[], currentValue: '', singleSelect: false, hint: '' };
  quickSelectModal = {
    open: false,
    title: '',
    description: '',
    buildingId: '',
    groups: [] as QuickSelectGroup[],
  };
  yearPickerModal = {
    open: false,
    title: '',
    buildingId: '',
    field: '' as BuildingFieldKey | '',
    selectedQualifier: '',
    selectedDecade: '',
    selectedYear: '',
  };
  appointmentPicker = {
    open: false,
    viewYear: new Date().getFullYear(),
    viewMonth: new Date().getMonth(),
    selectedDate: null as Date | null,
    selectedTime: '',
  };
  buildings: Building[] = [];
  allBuildings: Building[] = [];
  activeBuildingId: string | null = null;
  otherBuildings: OtherBuilding[] = [];
  activeOtherBuildingId: string | null = null;
  notesMode: 'text' | 'drawing' = 'text';
  drawing = false;
  lastPoint = { x: 0, y: 0 };
  channel: CameraChannel | null = null;
  heartbeatId: number | null = null;
  showCameraModal = false;
  cameraInitialImageName: string | null = null;
  cameraAllowBlankImageName = false;
  private lastRemoteJobNumber = '';
  private connectivitySubscription?: Subscription;
  private focusedCustomConflictPath: string | null = null;
  private unsavedBackPromptResolve: ((canLeave: boolean) => void) | null = null;
  private readonly internalLayoutSuffix = 'for this type of accommodation.';
  readonly floorAreaTitleOptions = [
    'Amenities',
    'Balcony',
    'Basement Garage',
    'Canopy',
    'Conservatory',
    'Deck',
    'Dwelling',
    'Integrated Garage',
    'Living',
    'Mezzanine',
    'Office',
    'Overall Enclosed Building',
    'Parking',
    'Residential Accommodation',
    'Store Shed',
    'Unit',
    'Workshop',
    'Ground Floor',
    'First Floor',
    'Second Floor',
    'Total',
  ];

  readonly fallbackValuationTypes: ValuationTypeOption[] = [
    { valuationTypeId: 'Standard', valuationType: 'Standard' },
    { valuationTypeId: 'Multiple-Dwelling (As If Complete)', valuationType: 'Multiple-Dwelling (As If Complete)' },
    { valuationTypeId: 'Standard (As If Complete)', valuationType: 'Standard (As If Complete)' },
    { valuationTypeId: 'Strata Unit', valuationType: 'Strata Unit' },
    { valuationTypeId: 'Strata Unit(As If Complete)', valuationType: 'Strata Unit(As If Complete)' },
    { valuationTypeId: 'Vacant Land', valuationType: 'Vacant Land' },
    { valuationTypeId: 'Vacant Land (As If Complete)', valuationType: 'Vacant Land (As If Complete)' },
    { valuationTypeId: 'Multi-Dwelling', valuationType: 'Multi-Dwelling' },
  ];
  valuationTypes: ValuationTypeOption[] = this.fallbackValuationTypes;

  readonly tabs: Array<{ id: TabId; label: string }> = [
    { id: 'general', label: 'General' },
    { id: 'improvements', label: 'Improvements' },
    { id: 'notes', label: 'Notes' },
  ];
  readonly reportTypeOptions = ['NZ_Rural Report'];

  get isJobAddressMissing(): boolean {
    return !this.job?.address?.trim();
  }

  get isReportTypeMissing(): boolean {
    const reportType = String(this.job?.reportType ?? '').trim().toLowerCase();
    if (!reportType || reportType === 'null' || reportType === 'undefined') return true;
    return !this.reportTypeOptions.some((option) => option.toLowerCase() === reportType);
  }

  get selectedReportType(): string {
    const reportType = String(this.job?.reportType ?? '').trim();
    return this.reportTypeOptions.includes(reportType) ? reportType : '';
  }

  isTabDisabled(tab: TabId): boolean {
    return tab === 'improvements' && this.isReportTypeMissing;
  }

  setReportType(value: string): void {
    if (!this.job) return;
    this.job.reportType = this.reportTypeOptions.includes(value) ? value : null;
    if (this.isReportTypeMissing && this.activeTab === 'improvements') this.activeTab = 'general';
  }

  readonly generalFields: FieldConfig[] = [
    { key: 'propertyType', label: 'Property Type', kind: 'option', optionList: 'propertyTypes', singleSelect: true },
    { key: 'siteDimensions', label: 'Site Dimensions', kind: 'text' },
    { key: 'neighbourhood', label: 'Neighbourhood', kind: 'option', optionList: 'neighbourhood' },
    { key: 'siteDescription', label: 'Site Description', kind: 'option', optionList: 'siteDescription' },
    { key: 'services', label: 'Services', kind: 'option', optionList: 'services' },
    { key: 'overallPresentation', label: 'Overall Presentation', kind: 'option', optionList: 'overallPresentation', singleSelect: true },
  ];

  readonly externalFields: FieldConfig[] = [
    { key: 'levels', label: 'Levels', kind: 'option', optionList: 'levels', singleSelect: true },
    { key: 'style', label: 'Style', kind: 'option', optionList: 'style' },
    { key: 'foundations', label: 'Foundations', kind: 'option', optionList: 'foundations' },
    { key: 'flooring', label: 'Flooring', kind: 'option', optionList: 'flooring' },
    { key: 'mainWalls', label: 'Main Walls', kind: 'option', optionList: 'mainWalls' },
    { key: 'windowFrames', label: 'Window Frames', kind: 'option', optionList: 'windowFrames' },
    { key: 'roof', label: 'Roof', kind: 'option', optionList: 'roof' },
    { key: 'interiorLinings', label: 'Interior Linings', kind: 'option', optionList: 'interiorLinings' },
    { key: 'ceilingLinings', label: 'Ceiling Linings', kind: 'option', optionList: 'ceilingLinings' },
  ];

  readonly internalFields: FieldConfig[] = [
    { key: 'bedrooms', label: 'Bedrooms', kind: 'number' },
    { key: 'bathrooms', label: 'Bathrooms', kind: 'number' },
    { key: 'livingAreas', label: 'Living Areas', kind: 'number' },
    { key: 'accommodationPlus', label: 'Accommodation Plus', kind: 'option', optionList: 'accommodationPlus' },
    { key: 'pcItems', label: 'PC Items', kind: 'option', optionList: 'pcItems' },
    { key: 'fixturesAndFeatures', label: 'Fixtures and Features', kind: 'option', optionList: 'fixturesAndFeatures' },
    { key: 'internalLayout', label: 'Internal Layout', kind: 'option', optionList: 'internalLayout', singleSelect: true },
    { key: 'internalCondition', label: 'Internal Condition', kind: 'option', optionList: 'internalCondition' },
  ];

  readonly areaFields: FieldConfig[] = [
    { key: 'livingAreasSqm', label: 'Living Areas sqm', kind: 'number' },
    { key: 'outdoorAreasSqm', label: 'Outdoor Areas sqm', kind: 'number' },
    { key: 'otherAreasSqm', label: 'Other Areas sqm', kind: 'number' },
    { key: 'carAreasSqm', label: 'Car Areas sqm', kind: 'number' },
    { key: 'externalCondition', label: 'External Condition', kind: 'option', optionList: 'externalCondition' },
    { key: 'ancillaryCondition', label: 'Ancillary Condition', kind: 'option', optionList: 'ancillaryCondition' },
    { key: 'yearOfConstruction', label: 'Year of Construction', kind: 'year' },
    { key: 'yearBuilt', label: 'Year Built', kind: 'year' },
    { key: 'yearOfAdditions', label: 'Year of Additions', kind: 'year' },
    { key: 'outdoorArea', label: 'Outdoor Area', kind: 'option', optionList: 'outdoorAreaOptionsList' },
    { key: 'undercoverCarSpaces', label: 'Undercover Car Spaces', kind: 'number' },
    { key: 'significantImprovements', label: 'Significant Improvements', kind: 'option', optionList: 'significantImprovements' },
    { key: 'secondaryImprovements', label: 'Secondary Improvements', kind: 'option', optionList: 'secondaryImprovementsOptions' },
    { key: 'essentialRepairs', label: 'Essential Repairs', kind: 'textarea' },
  ];

  readonly essentialRepairOptions = ['None', 'Yes - Refer Comments'];
  readonly appointmentWeekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  readonly appointmentTimes = this.buildAppointmentTimes();
  readonly yearQualifiers = ['Year Built', 'Built About', 'Circa', 'To Be Erected'];
  readonly yearDecades = ['1900-1909', '1910-1919', '1920-1929', '1930-1939', '1940-1949', '1950-1959', '1960-1969', '1970-1979', '1980-1989', '1990-1999', '2000-2009', '2010-2019', '2020-2029'];
  readonly internalLayoutBuildingField: BuildingFieldConfig = { key: 'internalLayout', label: 'Internal Layout', kind: 'option', optionList: 'internalLayout', singleSelect: true };
  readonly internalConditionBuildingField: BuildingFieldConfig = { key: 'internalCondition', label: 'Internal Condition', kind: 'option', optionList: 'internalCondition', singleSelect: true };
  readonly externalConditionBuildingField: BuildingFieldConfig = { key: 'externalCondition', label: 'External Condition', kind: 'option', optionList: 'externalCondition', singleSelect: true };
  readonly mainCarAccomField: FieldConfig = { key: 'mainCarAccomType', label: 'Car Accommodation', kind: 'option', optionList: 'mainCarAccomType' };
  readonly yearConstructionBuildingField: BuildingFieldConfig = { key: 'yearOfConstruction', label: 'Year of Construction', kind: 'option', optionList: 'yearOptions', singleSelect: true };
  readonly yearAdditionsBuildingField: BuildingFieldConfig = { key: 'yearOfAdditions', label: 'Year Most Recent Additions Constructed', kind: 'option', optionList: 'yearOptions', singleSelect: true };
  readonly significantImprovementsBuildingField: BuildingFieldConfig = { key: 'significantImprovements', label: 'Other Improvements (Significant Ancillary)', kind: 'textarea', optionList: 'significantImprovements' };
  readonly secondaryImprovementsBuildingField: BuildingFieldConfig = { key: 'secondaryImprovements', label: 'Other Improvements (Secondary Ancillary)', kind: 'textarea', optionList: 'secondaryImprovementsOptions' };

  get selectedDecadeYears(): number[] {
    if (!this.yearPickerModal.selectedDecade) return [];
    const start = Number(this.yearPickerModal.selectedDecade.split('-')[0]);
    return Array.from({ length: 10 }, (_, index) => start + index);
  }

  readonly externalBuildingFields: BuildingFieldConfig[] = [
    { key: 'levels', label: 'Levels', kind: 'option', optionList: 'levels', singleSelect: true },
    { key: 'style', label: 'Style', kind: 'option', optionList: 'style' },
    { key: 'foundations', label: 'Foundations', kind: 'option', optionList: 'foundations' },
    { key: 'flooring', label: 'Flooring', kind: 'option', optionList: 'flooring' },
    { key: 'mainWalls', label: 'Main Walls', kind: 'option', optionList: 'mainWalls' },
    { key: 'windowFrames', label: 'Window Frames', kind: 'option', optionList: 'windowFrames' },
    { key: 'roof', label: 'Roof', kind: 'option', optionList: 'roof' },
    { key: 'interiorLinings', label: 'Interior Linings', kind: 'option', optionList: 'interiorLinings' },
    { key: 'ceilingLinings', label: 'Ceiling Linings', kind: 'option', optionList: 'ceilingLinings' },
    { key: 'frame', label: 'Frame', kind: 'option', optionList: 'frame' },
  ];

  readonly internalBuildingSummaryFields: BuildingFieldConfig[] = [
    { key: 'bedrooms', label: 'No. of Bedrooms', kind: 'number' },
    { key: 'bathrooms', label: 'No. of Bathrooms', kind: 'number' },
    { key: 'numberOfLivingAreas', label: 'Number of Living Areas', kind: 'number' },
    { key: 'undercoverCarSpaces', label: 'No. of Undercover Car Spaces', kind: 'number' },
    { key: 'carAccommodation', label: 'Car Accommodation', kind: 'option', optionList: 'mainCarAccomType', singleSelect: true },
  ];

  readonly internalBuildingTextFields: BuildingFieldConfig[] = [
    { key: 'pcItems', label: 'Accommodation Description', kind: 'textarea' },
    { key: 'features', label: 'Additional Features', kind: 'textarea' },
    { key: 'chattels', label: 'Chattels', kind: 'textarea' },
    { key: 'briefDescription', label: 'Improvement Overview', kind: 'textarea' },
  ];

  readonly internalBuildingFields: BuildingFieldConfig[] = [
    ...this.internalBuildingSummaryFields,
    ...this.internalBuildingTextFields,
  ];

  readonly additionalBuildingFields: BuildingFieldConfig[] = [
    { key: 'livingAreasSqm', label: 'Living Areas (m2)', kind: 'number' },
    { key: 'outdoorAreasSqm', label: 'Outdoor Areas (m2)', kind: 'number' },
    { key: 'otherAreasSqm', label: 'Other Areas (m2)', kind: 'number' },
    { key: 'carAreasSqm', label: 'Car Areas (m2)', kind: 'number' },
    { key: 'internalLayout', label: 'Internal Layout', kind: 'option', optionList: 'internalLayout', singleSelect: true },
    { key: 'internalCondition', label: 'Internal Condition', kind: 'option', optionList: 'internalCondition', singleSelect: true },
    { key: 'externalCondition', label: 'External Condition', kind: 'option', optionList: 'externalCondition', singleSelect: true },
    { key: 'yearOfConstruction', label: 'Year of Construction', kind: 'option', optionList: 'yearOptions', singleSelect: true },
    { key: 'yearOfAdditions', label: 'Year Most Recent Additions Constructed', kind: 'option', optionList: 'yearOptions', singleSelect: true },
    { key: 'outdoorArea', label: 'Outdoor Area', kind: 'text' },
    { key: 'significantImprovements', label: 'Other Improvements (Significant Ancillary)', kind: 'textarea', optionList: 'significantImprovements' },
    { key: 'secondaryImprovements', label: 'Other Improvements (Secondary Ancillary)', kind: 'textarea', optionList: 'secondaryImprovementsOptions' },
  ];

  readonly ruralFields: FieldConfig[] = [
    { key: 'water', label: 'Water', kind: 'option', optionList: 'waterOptions' },
    { key: 'drainage', label: 'Drainage', kind: 'option', optionList: 'drainageOptions' },
    { key: 'irrigation', label: 'Irrigation', kind: 'text' },
    { key: 'fencing', label: 'Fencing', kind: 'option', optionList: 'fencingOptions' },
    { key: 'races', label: 'Races', kind: 'option', optionList: 'racesOptions' },
  ];
  readonly essentialRepairCommentsField: FieldConfig = { key: 'essentialRepairComments', label: 'Essential Repair Comments', kind: 'textarea', optionList: 'essentialRepairComments' };

  readonly form = this.fb.group({
    jobId: [0],
    valuationType: [''], valuationTypeId: [''], valuationTypeDescription: [''], valuationTypes: [[] as ValuationTypeOption[]],
    propertyType: [''], siteDimensions: [''], neighbourhood: [''], siteDescription: [''], services: [''],
    style: [''], levels: [''], streetAppeal: [''], foundations: [''], flooring: [''], mainWalls: [''], windowFrames: [''], roof: [''], interiorLinings: [''], ceilingLinings: [''],
    bedrooms: [null as number | null], bathrooms: [null as number | null], livingAreas: [null as number | null], accommodationPlus: [''], pcItems: [''], fixturesAndFeatures: [''],
    livingAreasSqm: [null as number | null], outdoorAreasSqm: [null as number | null], otherAreasSqm: [null as number | null], carAreasSqm: [null as number | null], internalLayout: [''], internalCondition: [''], externalCondition: [''], ancillaryCondition: [''], mainCarAccomType: [''],
    yearOfConstruction: [''], yearBuilt: [''], yearOfAdditions: [''], outdoorArea: [''], undercoverCarSpaces: [null as number | null], significantImprovements: [''], secondaryImprovements: [''], essentialRepairs: [''],
    rural: this.fb.group({ water: [''], drainage: [''], irrigation: [''], fencing: [''], races: [''], essentialRepairRequired: ['None'], essentialRepairComments: [''] }),
    buildings: ['[]'], otherBuildings: ['[]'], notes: [''], drawingData: [''], overallPresentation: [''], carSpaces: [null as number | null], clientNameContactDetails: [''], clientCompanyName: [''], clientReportPreparedFor: [''], clientContactName: [''], clientPhone: [''], clientEmail: [''], squareFootage: [null as number | null], inspectionChecklist: [''],
  });

  ngOnInit(): void {
    this.jobId = Number(this.route.snapshot.paramMap.get('id') || 0);
    this.form.valueChanges.subscribe(() => this.unsaved.set(true));
    this.connectivitySubscription = this.connectivity.isOnline$.subscribe((isOnline) => {
      this.isOnline = isOnline;
    });
    this.setupCameraChannel();
    this.load();
  }

  ngAfterViewInit(): void {
    this.restoreDrawingAfterRender();
  }

  ngOnDestroy(): void {
    this.connectivitySubscription?.unsubscribe();
    this.closeCameraContext();
  }

  load(): void {
    this.loading = true;
    forkJoin({ job: this.api.getJob(this.jobId), info: this.api.getPropertyInfo(this.jobId) }).subscribe({
      next: ({ job, info }) => {
        this.job = job;
        this.patchForm(info || { jobId: this.jobId });
        this.loading = false;
        this.unsaved.set(false);
      },
      error: (error) => {
        this.loading = false;
        this.toast.error('Error', error?.message || 'Failed to load job.');
      },
    });
  }

  patchForm(info: Partial<PropertyInfo>): void {
    this.valuationTypes = this.normalizeValuationTypes(
      info.valuationTypes,
      info.valuationTypeId || info.valuationType || '',
      info.valuationTypeDescription || '',
    );
    this.form.patchValue({ ...info, rural: this.normalizeRuralInfo(info), jobId: this.jobId } as Record<string, unknown>);
    this.setValuationType(
      this.form.controls.valuationType.value || this.form.controls.valuationTypeId.value || '',
      this.form.controls.valuationTypeDescription.value || '',
    );
    this.allBuildings = this.dedupeDwellingsByName(info.buildings ? this.parseJsonArray<Building>(info.buildings, []) : []).map((building) => this.normalizeBuilding(building));
    this.buildings = this.allBuildings.filter(building => building.isDelete === false);
    this.activeBuildingId = this.buildings[0]?.id || null;
    this.ensureActiveBuildingVisible();
    this.otherBuildings = this.parseJsonArray<OtherBuilding>(this.form.controls.otherBuildings.value, []);
    this.activeOtherBuildingId = this.otherBuildings[0]?.id || null;
    this.restoreDrawingAfterRender();
  }

  normalizeRuralInfo(info: Partial<PropertyInfo>): RuralInfo {
    const legacyInfo = info as Record<string, unknown>;
    const legacyEssentialRepairs = String(legacyInfo['essentialRepairs'] ?? '').trim();
    const savedEssentialRepairRequired = String(info.rural?.essentialRepairRequired ?? '').trim();
    const legacyEssentialRepairRequired = /^yes$/i.test(legacyEssentialRepairs)
      ? 'Yes - Refer Comments'
      : /^no$|^none$/i.test(legacyEssentialRepairs)
        ? 'None'
        : legacyEssentialRepairs
          ? 'Yes - Refer Comments'
          : 'None';
    const essentialRepairRequired = this.normalizeEssentialRepairRequired(savedEssentialRepairRequired || legacyEssentialRepairRequired);
    const essentialRepairComments = info.rural?.essentialRepairComments
      ?? (/^yes$|^no$|^none$/i.test(legacyEssentialRepairs) ? '' : legacyEssentialRepairs);
    return {
      water: info.rural?.water ?? legacyInfo['water'] as string ?? '',
      drainage: info.rural?.drainage ?? legacyInfo['drainage'] as string ?? '',
      irrigation: info.rural?.irrigation ?? legacyInfo['irrigation'] as string ?? '',
      fencing: info.rural?.fencing ?? legacyInfo['fencing'] as string ?? '',
      races: info.rural?.races ?? legacyInfo['races'] as string ?? '',
      essentialRepairRequired,
      essentialRepairComments,
    };
  }

  normalizeValuationTypes(value: ValuationTypeOption[] | null | undefined, selectedId = '', selectedDescription = ''): ValuationTypeOption[] {
    return Array.isArray(value) && value.length ? value : this.fallbackValuationTypes;
  }

  setValuationType(value: string, description = ''): void {
    const selected = this.valuationTypes.find((option) =>
      String(option.valuationTypeId) === String(value)
      || String(option.valuationType) === String(value)
      || String(option.valuationType) === String(description)
    );
    this.form.patchValue({
      valuationType: selected?.valuationTypeId || value || '',
      valuationTypeId: selected?.valuationTypeId || value || '',
      valuationTypeDescription: selected?.valuationType || '',
    }, { emitEvent: false });
    this.ensureActiveBuildingVisible();
  }

  get isMultiDwellingValuationType(): boolean {
    const valuationType = this.form.controls.valuationType.value || '';
    const selected = this.valuationTypes.find((option) => String(option.valuationTypeId) === String(valuationType));
    const normalizedValues = [
      valuationType,
      selected?.valuationTypeId,
      selected?.valuationType,
      this.form.controls.valuationTypeDescription.value,
    ].map((item) => this.normalizeValuationTypeText(item));
    return normalizedValues.some((value) =>
      value === 'multidwelling' ||
      value === 'multidwellingasifcomplete' ||
      value === 'multipledwelling' ||
      value === 'multipledwellingasifcomplete'
    );
  }

  get visibleBuildings(): Building[] {
    return this.buildings.filter((building) => this.isMainDwelling(building) || this.isMultiDwellingValuationType);
  }

  isMainDwelling(building: Building): boolean {
    return String(building.name || '').trim().toLowerCase() === 'main dwelling';
  }

  get isEssentialRepairRequired(): boolean {
    const value = this.normalizeEssentialRepairText(this.form.controls.rural.controls.essentialRepairRequired.value);
    return value === 'yes' || value === 'yesrefercomments';
  }

  private normalizeEssentialRepairRequired(value: unknown): string {
    const normalizedValue = this.normalizeEssentialRepairText(value);
    return normalizedValue === 'yes' || normalizedValue === 'yesrefercomments' ? 'Yes - Refer Comments' : 'None';
  }

  private normalizeEssentialRepairText(value: unknown): string {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  onEssentialRepairRequiredChange(): void {
    if (!this.isEssentialRepairRequired) this.form.controls.rural.controls.essentialRepairComments.setValue('');
  }

  private propertyInfoPayload(): PropertyInfo {
    const raw = cleanFormValue(this.form.getRawValue() as Record<string, unknown>);
    const numericKeys = ['bedrooms', 'bathrooms', 'livingAreas', 'livingAreasSqm', 'outdoorAreasSqm', 'otherAreasSqm', 'carAreasSqm', 'undercoverCarSpaces', 'carSpaces', 'squareFootage'];
    numericKeys.forEach((key) => raw[key] = coerceNumber(raw[key]));
    raw['jobId'] = this.jobId; 
    raw['valuationTypeId'] = raw['valuationType'];
    raw['valuationTypeDescription'] = this.valuationTypes.find((option) => String(option.valuationTypeId) === String(raw['valuationType']))?.valuationType || '';
    raw['valuationTypes'] = this.valuationTypes;
    this.allBuildings = this.dedupeDwellingsByName(this.allBuildings.map((building) => this.normalizeBuilding(building)));
    this.buildings = this.allBuildings.filter(building => building.isDelete === false);
    raw['buildings'] = JSON.stringify(this.allBuildings);
    raw['otherBuildings'] = JSON.stringify(this.otherBuildings.map((building) => ({
      ...building,
      area: this.normalizeOtherBuildingArea(building.area),
    })));
    raw['rural'] = cleanFormValue(raw['rural'] as Record<string, unknown>);
    return raw as unknown as PropertyInfo;
  }

  save(): void {
    const payload = this.propertyInfoPayload();
    
    this.saving = true;
    this.api.savePropertyInfo(this.jobId, payload).subscribe({
      next: () => {
        this.saving = false;
        this.unsaved.set(false);
        this.toast.success('Success', 'Property information saved.');
        // this.saveMultiDwellingToBackend(payload as unknown as Record<string, unknown>);
      },
      error: (error) => {
        this.saving = false;
        this.toast.error('Error', error?.message || 'Failed to save property information.');
      },
    });
  }

  private saveMultiDwellingToBackend(raw: Record<string, unknown>): void {
    const trackerId = this.job?.jobNumber?.trim();
    if (!trackerId || !this.allBuildings.length) return;
    const payload: ImprovementsPayload = {
      multiDwelling: this.allBuildings.filter((building) => building.name !== 'Main Dwelling'),
      rural: raw['rural'] as RuralInfo,
      otherBuildings: this.otherBuildings,
    };
    this.api.saveMultiDwelling(trackerId, payload).pipe(
      catchError((error) => {
        this.toast.error('Error', error?.message || 'Failed to save multi-dwelling information.');
        this.allBuildings = this.parseJsonArray<Building>(this.form.controls.buildings.value, []);
        this.buildings = this.allBuildings.filter(building => building.isDelete === false);
        return of(null);
      }),
    ).subscribe((updatedBuildings) => {
      if (!Array.isArray(updatedBuildings) || !updatedBuildings.length) return;
      this.buildings = updatedBuildings;
      const updatedRaw = { ...raw, buildings: JSON.stringify(this.allBuildings) };
      this.api.savePropertyInfo(this.jobId, updatedRaw as unknown as PropertyInfo).subscribe();
    });
  }

  saveAll(): void {
    if (this.isJobAddressMissing) {
      this.toast.error('Address required', 'Address is mandatory for jobs.');
      return;
    }
    this.saveJobHeader();
    this.save();
  }

  requestBackToJobs(): void {
    void this.router.navigate(['/']);
  }

  canDeactivate(): boolean | Promise<boolean> {
    if (!this.unsaved.hasUnsavedChanges()) return true;
    if (this.unsavedBackPromptOpen) return false;
    this.unsavedBackPromptOpen = true;
    return new Promise((resolve) => {
      this.unsavedBackPromptResolve = resolve;
    });
  }

  cancelBackToJobs(): void {
    this.unsavedBackPromptOpen = false;
    this.unsavedBackPromptResolve?.(false);
    this.unsavedBackPromptResolve = null;
  }

  proceedBackToJobsWithoutSaving(): void {
    this.unsavedBackPromptOpen = false;
    this.unsaved.set(false);
    this.unsavedBackPromptResolve?.(true);
    this.unsavedBackPromptResolve = null;
  }

  openManagePhotos(): void {
    if (!this.job || this.saving || this.hasDuplicateNames()) return;
    const payload = this.propertyInfoPayload();
    this.saving = true;
    this.api.savePropertyInfo(this.jobId, payload).subscribe({
      next: () => {
        this.saving = false;
        this.unsaved.set(false);
        void this.router.navigate(['/jobs', this.jobId, 'photos']);
      },
      error: (error) => {
        this.saving = false;
        this.toast.error('Error', error?.message || 'Failed to save property information before opening photos.');
      },
    });
  }

  saveAndSync(): void {
    if (!this.job || this.saving || this.syncing || this.syncChoiceOpen) return;
    if (!this.isOnline) {
      this.toast.error('Offline', 'Sync to Upload is available only when this device is online.');
      return;
    }
    if (this.isJobAddressMissing) {
      this.toast.error('Address required', 'Address is mandatory for jobs.');
      return;
    }
    if (!this.job.jobNumber?.trim()) {
      this.toast.error('Job number required', 'TOPS Job Number is required before syncing.');
      return;
    }
    if (this.hasDuplicateNames()) {
      this.toast.error('Duplicate dwelling names', 'Resolve duplicate dwelling names before syncing.');
      return;
    }

    this.syncContentMode = 'fields-and-photos';
    this.api.getPendingUploadPhotos(this.job.id).subscribe({
      next: (photos) => {
        this.syncPhotoCount = photos.length;
        this.syncChoiceOpen = true;
      },
      error: () => {
        this.syncPhotoCount = 0;
        this.syncChoiceOpen = true;
      },
    });
  }

  closeSyncChoice(): void {
    if (this.syncing) return;
    this.syncChoiceOpen = false;
  }

  startSync(): void {
    if (!this.job || this.syncing) return;
    this.syncChoiceOpen = false;
    this.syncing = true;
    this.saveCurrentJobForSync(this.job).pipe(
      switchMap((job) => this.api.prepareJobSync(job)),
      finalize(() => {
        if (!this.syncReviewOpen) this.syncing = false;
      }),
    ).subscribe({
      next: (draft) => {
        if (this.syncContentMode === 'photos-only') {
          this.syncResolvedJob(draft, [], this.syncContentMode);
          return;
        }
        if (draft.conflicts.length) {
          this.syncDraft = draft;
          this.syncConflicts = draft.conflicts;
          this.syncReviewOpen = true;
          this.syncing = false;
          return;
        }
        this.syncResolvedJob(draft, [], this.syncContentMode);
      },
      error: (error) => this.toast.error('Save and Sync failed', error?.message || 'Failed to prepare job sync.'),
    });
  }

  confirmJobSync(): void {
    if (!this.syncDraft || this.syncing) return;
    this.syncResolvedJob(this.syncDraft, this.syncConflicts, this.syncContentMode);
  }

  hasCustomConflictValue(conflict: JobSyncConflict): boolean {
    return conflict.customValue !== undefined && conflict.customValue !== '';
  }

  isCustomConflictActive(conflict: JobSyncConflict): boolean {
    return this.hasCustomConflictValue(conflict) || this.focusedCustomConflictPath === conflict.path;
  }

  focusCustomConflict(conflict: JobSyncConflict): void {
    this.focusedCustomConflictPath = conflict.path;
  }

  blurCustomConflict(conflict: JobSyncConflict): void {
    if (this.focusedCustomConflictPath === conflict.path) this.focusedCustomConflictPath = null;
  }

  selectConflictValue(conflict: JobSyncConflict, selected: 'server' | 'local'): void {
    conflict.selected = selected;
    conflict.customValue = '';
    if (this.focusedCustomConflictPath === conflict.path) this.focusedCustomConflictPath = null;
  }

  closeSyncReview(force = false): void {
    if (this.syncing && !force) return;
    this.syncDraft = null;
    this.syncConflicts = [];
    this.syncReviewOpen = false;
  }

  valueText(value: unknown): string {
    if (value === null || value === undefined || value === '') return 'Blank';
    if (typeof value === 'string') return value.startsWith('data:image/') ? '[image data]' : value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return value.map((item) => this.valueText(item)).join(', ');
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  }

  imageSource(value: unknown): string | null {
    return typeof value === 'string' && value.startsWith('data:image/') ? value : null;
  }

  private saveCurrentJobForSync(job: Job) {
    const address = job.address?.trim();
    const { appointmentDate, appointmentTime } = parseAppointment(this.displayAppointment());
    const propertyInfo = this.propertyInfoPayload();
    this.saving = true;
    return forkJoin({
      job: this.api.updateJob(job.id, {
        jobNumber: job.jobNumber,
        address: address || job.address,
        reportType: job.reportType || null,
        appointmentDate,
        appointmentTime,
        status: job.status || 'In Progress',
      }),
      info: this.api.savePropertyInfo(this.jobId, propertyInfo),
    }).pipe(
      finalize(() => this.saving = false),
      switchMap(({ job }) => {
        this.job = job;
        this.unsaved.set(false);
        return of(job);
      }),
    );
  }

  private syncResolvedJob(draft: JobSyncDraft, conflicts: JobSyncConflict[], contentMode: JobSyncContentMode): void {
    this.syncing = true;
    this.api.syncResolvedJobToSqs(draft, conflicts, contentMode).pipe(
      switchMap((result) => forkJoin({
        result: of(result),
        info: this.api.getPropertyInfo(this.jobId),
      })),
      finalize(() => this.syncing = false),
    ).subscribe({
      next: ({ result, info }) => {
        this.patchForm(info || { jobId: this.jobId });
        this.unsaved.set(false);
        this.closeSyncReview(true);
        this.syncSuccessOpen = true;
        this.toast.success('Save and Sync complete', `Job ${draft.jobNumber || draft.jobId} sent to sync${result.messageId ? ` (${result.messageId})` : ''}.`);
      },
      error: (error) => this.toast.error('Save and Sync failed', error?.message || 'Failed to sync this job.'),
    });
  }

  closeSyncSuccess(): void {
    this.syncSuccessOpen = false;
  }

  saveJobHeader(): void {
    if (!this.job) return;
    const address = this.job.address?.trim();
    if (!address) {
      this.toast.error('Address required', 'Address is mandatory for jobs.');
      return;
    }
    const { appointmentDate, appointmentTime } = parseAppointment(this.displayAppointment());
    this.api.updateJob(this.job.id, {
      jobNumber: this.job.jobNumber,
      address,
      reportType: this.job.reportType || null,
      appointmentDate,
      appointmentTime,
      status: this.job.status || 'In Progress',
    }).subscribe({
      next: (job) => { this.job = job; this.toast.success('Success', 'Job header saved.'); },
      error: (error) => this.toast.error('Error', error?.message || 'Failed to update job.'),
    });
  }

  displayAppointment(): string {
    if (!this.job?.appointmentDate) return '';
    const date = formatDisplayDate(this.job.appointmentDate);
    return this.job.appointmentTime ? `${date} ${this.job.appointmentTime}` : date;
  }

  setAppointment(value: string): void {
    if (!this.job) return;
    const parsed = parseAppointment(value);
    this.job = { ...this.job, appointmentDate: parsed.appointmentDate, appointmentTime: parsed.appointmentTime };
  }

  openAppointmentPicker(): void {
    if (!this.job) return;
    const selectedDate = this.parseDisplayDate(this.job.appointmentDate) || new Date();
    this.appointmentPicker = {
      open: true,
      viewYear: selectedDate.getFullYear(),
      viewMonth: selectedDate.getMonth(),
      selectedDate,
      selectedTime: this.job.appointmentTime || this.nearestAppointmentTime(new Date()),
    };
  }

  closeAppointmentPicker(): void {
    this.appointmentPicker.open = false;
  }

  appointmentMonthLabel(): string {
    return new Date(this.appointmentPicker.viewYear, this.appointmentPicker.viewMonth, 1).toLocaleString('en-NZ', { month: 'long', year: 'numeric' });
  }

  appointmentCalendarDays(): CalendarDay[] {
    const firstOfMonth = new Date(this.appointmentPicker.viewYear, this.appointmentPicker.viewMonth, 1);
    const start = new Date(firstOfMonth);
    start.setDate(firstOfMonth.getDate() - firstOfMonth.getDay());
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return { date, day: date.getDate(), currentMonth: date.getMonth() === this.appointmentPicker.viewMonth };
    });
  }

  changeAppointmentMonth(offset: number): void {
    const next = new Date(this.appointmentPicker.viewYear, this.appointmentPicker.viewMonth + offset, 1);
    this.appointmentPicker.viewYear = next.getFullYear();
    this.appointmentPicker.viewMonth = next.getMonth();
  }

  selectAppointmentDate(date: Date): void {
    this.appointmentPicker.selectedDate = new Date(date);
    this.appointmentPicker.viewYear = date.getFullYear();
    this.appointmentPicker.viewMonth = date.getMonth();
  }

  selectAppointmentTime(time: string): void {
    this.appointmentPicker.selectedTime = time;
  }

  selectAppointmentToday(): void {
    this.selectAppointmentDate(new Date());
  }

  selectAppointmentTimeNow(): void {
    this.appointmentPicker.selectedTime = this.nearestAppointmentTime(new Date());
  }

  applyAppointmentPicker(): void {
    if (!this.job || !this.appointmentPicker.selectedDate) return;
    const date = this.formatDateForAppointment(this.appointmentPicker.selectedDate);
    this.job = { ...this.job, appointmentDate: date, appointmentTime: this.appointmentPicker.selectedTime || null };
    this.unsaved.set(true);
    this.closeAppointmentPicker();
  }

  isSelectedAppointmentDate(date: Date): boolean {
    return !!this.appointmentPicker.selectedDate && this.sameCalendarDate(date, this.appointmentPicker.selectedDate);
  }

  isTodayAppointmentDate(date: Date): boolean {
    return this.sameCalendarDate(date, new Date());
  }

  setAppointmentNow(): void {
    if (!this.job) return;
    const now = new Date();
    const pad = (value: number) => value.toString().padStart(2, '0');
    const appointment = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    this.setAppointment(appointment);
  }

  private buildAppointmentTimes(): string[] {
    return Array.from({ length: 48 }, (_, index) => {
      const totalMinutes = index * 30;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    });
  }

  private nearestAppointmentTime(date: Date): string {
    const totalMinutes = date.getHours() * 60 + date.getMinutes();
    const roundedMinutes = Math.min(23 * 60 + 30, Math.round(totalMinutes / 30) * 30);
    const hours = Math.floor(roundedMinutes / 60);
    const minutes = roundedMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private parseDisplayDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    const displayDate = formatDisplayDate(value);
    const match = displayDate.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (!match) return null;
    return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
  }

  private formatDateForAppointment(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
  }

  private sameCalendarDate(first: Date, second: Date): boolean {
    return first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth() && first.getDate() === second.getDate();
  }

  lookupRemoteJobFromHeader(event?: Event): void {
    event?.preventDefault();
    const jobNumber = this.job?.jobNumber?.trim() || '';
    if (!this.job || !jobNumber || this.fetchingRemoteJob || this.lastRemoteJobNumber === jobNumber) return;
    const currentJob = this.job;
    this.fetchingRemoteJob = true;
    this.api.getRemoteJobDetails(jobNumber).pipe(
      switchMap((details) => {
        const nextJob = {
          ...currentJob,
          ...details.job,
          id: currentJob.id,
          photoUrl: currentJob.photoUrl,
        };
        this.job = nextJob;
        this.form.patchValue(details.propertyInfo as Record<string, unknown>);
        this.unsaved.set(true);
        return forkJoin({
          job: this.api.updateJob(currentJob.id, details.job),
          info: this.api.savePropertyInfo(currentJob.id, {
            ...this.form.getRawValue(),
            ...details.propertyInfo,
            valuationTypes: details.valuationTypes,
            jobId: currentJob.id,
          } as PropertyInfo),
          photos: this.api.saveImportedPhotos(currentJob.id, details.photos),
        });
      }),
      finalize(() => this.fetchingRemoteJob = false),
    ).subscribe({
      next: ({ job, info }) => {
        this.lastRemoteJobNumber = jobNumber;
        this.job = job;
        this.patchForm(info);
        this.unsaved.set(false);
        this.toast.success('Job loaded', `Details for job ${jobNumber} have been pre-filled.`);
      },
      error: (error) => {
        this.lastRemoteJobNumber = '';
        this.toast.error('Remote lookup failed', this.remoteLookupErrorMessage(error, jobNumber));
      },
    });
  }

  private remoteLookupErrorMessage(error: unknown, jobNumber: string): string {
    const message = error instanceof Error ? error.message : '';
    if (/no records exist/i.test(message)) return `Job ${jobNumber} was not found or the remote service is offline.`;
    return message || `Job ${jobNumber} was not found or the remote service is offline.`;
  }

  setActiveTab(tab: TabId): void {
    if (this.isTabDisabled(tab)) {
      this.activeTab = 'general';
      return;
    }
    this.activeTab = tab;
    if (tab === 'notes' && this.notesMode === 'drawing') this.restoreDrawingAfterRender();
  }

  setNotesMode(mode: 'text' | 'drawing'): void {
    this.notesMode = mode;
    if (mode === 'drawing') this.restoreDrawingAfterRender();
  }

  copyField(value: unknown): void {
    const text = typeof value === 'string' ? value : '';
    navigator.clipboard?.writeText(text).then(() => {
      this.toast.success('Copied', 'Field value copied to clipboard.');
    }).catch(() => this.toast.error('Copy failed', 'Unable to copy this field.'));
  }

  openOptions(field: FieldConfig): void {
    if (!field.optionList) return;
    this.options.get(field.optionList).subscribe((options) => {
      this.optionModal = {
        open: true,
        title: field.label,
        field: field.key,
        options,
        optionGroups: [],
        currentValue: this.optionModalCurrentValue(field.key, field.key, true),
        singleSelect: !!field.singleSelect,
        hint: this.optionModalHint(field.key),
      };
    });
  }

  openRuralOptions(field: FieldConfig): void {
    if (!field.optionList) return;
    this.options.get(field.optionList).subscribe((options) => {
      this.optionModal = {
        open: true,
        title: field.label,
        field: `rural.${field.key}`,
        options,
        optionGroups: [],
        currentValue: this.optionModalCurrentValue(`rural.${field.key}`, field.key, true),
        singleSelect: !!field.singleSelect,
        hint: this.optionModalHint(field.key),
      };
    });
  }

  openBuildingOptions(building: Building, field: BuildingFieldConfig): void {
    if (field.key === 'yearOfConstruction' || field.key === 'yearOfAdditions') {
      this.openYearPicker(building, field.key, field.label);
      return;
    }
    if (!field.optionList) return;
    this.options.get(field.optionList).subscribe((options) => {
      const secondaryGroups = field.key === 'secondaryImprovements' ? this.secondaryImprovementsOptionGroups(options) : [];
      this.optionModal = {
        open: true,
        title: field.label,
        field: `building:${building.id}:${field.key}`,
        options,
        optionGroups: secondaryGroups,
        currentValue: secondaryGroups.length
          ? this.secondaryImprovementsSelectionValue(String(this.buildingFieldValue(building, field.key) ?? ''), secondaryGroups)
          : this.optionModalCurrentValue(String(this.buildingFieldValue(building, field.key) ?? ''), field.key),
        singleSelect: !!field.singleSelect,
        hint: this.optionModalHint(field.key),
      };
    });
  }

  openOtherBuildingOptions(building: OtherBuilding, key: OtherBuildingFieldKey, title: string, optionList: OptionListName): void {
    this.options.get(optionList).subscribe((options) => {
      this.optionModal = {
        open: true,
        title,
        field: `otherBuilding:${building.id}:${key}`,
        options,
        optionGroups: optionList === 'otherBuildingPcItemsOptions' ? this.pcItemsOptionGroups(options) : [],
        currentValue: optionList === 'otherBuildingPcItemsOptions' ? this.pcItemsSelectionValue(String(building[key] ?? ''), options) : String(building[key] ?? ''),
        singleSelect: false,
        hint: '',
      };
    });
  }

  private pcItemsOptionGroups(options: string[]): OptionGroup[] {
    const conditionOptions = ['Excellent', 'Good', 'Average', 'Fair'];
    const conditionSet = new Set(conditionOptions.map((option) => option.toLowerCase()));
    const availableConditions = conditionOptions.filter((condition) => options.some((option) => option.toLowerCase() === condition.toLowerCase()));
    const pcItems = options.filter((option) => !conditionSet.has(option.toLowerCase()));

    return [
      { label: 'Condition', options: availableConditions },
      { label: 'PC Items', options: pcItems },
    ].filter((group) => group.options.length);
  }

  private pcItemsSelectionValue(value: string, options: string[]): string {
    const normalizedValue = value.toLowerCase();
    return joinCsv(
      options.filter((option) => normalizedValue.includes(option.toLowerCase())),
      options,
    );
  }

  private secondaryImprovementsOptionGroups(options: string[]): OptionGroup[] {
    const groups = [
      { key: 'retainingWalls', label: 'Retaining walls', suffix: 'retaining walls' },
      { key: 'fencing', label: 'Fencing', suffix: 'fencing' },
      { key: 'driveway', label: 'Driveway', suffix: 'driveway' },
      { key: 'landscaping', label: 'Landscaping', suffix: 'landscaping' },
      { key: 'communalArea', label: 'Communal Area Improvements(for units)', suffix: 'common area improvements include' },
      { key: 'other', label: 'Other', suffix: 'Other' },
    ];
    const ranges = [9, 11, 13, 8, 9, 6];
    let start = 0;
    return groups.map((group, index) => {
      const groupOptions = options.slice(start, start + ranges[index]);
      start += ranges[index];
      return {
        label: group.label,
        options: groupOptions.map((option) => ({ label: option, value: `${group.key}|${option}` })),
      };
    }).filter((group) => group.options.length);
  }

  private secondaryImprovementsSelectionValue(value: string, groups: OptionGroup[]): string {
    const normalizedValue = value.toLowerCase();
    const rawSelections = splitCsv(value);
    const selected = groups.flatMap((group) =>
      group.options.filter((option) => {
        const item = option as OptionItem;
        const parsed = this.parseSecondaryImprovementSelection(item.value);
        const sentencePart = this.secondaryImprovementSentencePart(item.value).toLowerCase();
        const canMatchLabel = parsed?.group === 'communalArea' || parsed?.group === 'other';
        return rawSelections.includes(item.value) || rawSelections.includes(item.label) || (!!sentencePart && normalizedValue.includes(sentencePart)) || (canMatchLabel && normalizedValue.includes(item.label.toLowerCase()));
      }).map((option) => (option as OptionItem).value),
    );
    return joinCsv(selected, groups.flatMap((group) => group.options.map((option) => (option as OptionItem).value)));
  }

  private formatSecondaryImprovementsValue(value: string): string {
    const selections = splitCsv(value).map((item) => this.parseSecondaryImprovementSelection(item)).filter((item): item is { group: string; option: string } => !!item);
    const regular = selections.filter((item) => item.group !== 'other' && item.group !== 'communalArea');
    const communal = selections.filter((item) => item.group === 'communalArea').map((item) => item.option);
    const other = selections.filter((item) => item.group === 'other').map((item) => item.option);
    const parts = regular.map((item) => this.secondaryImprovementSentencePart(`${item.group}|${item.option}`)).filter(Boolean);

    let sentence = this.joinSentenceItems(parts);
    if (communal.length) {
      const communalText = `common area improvements include ${this.joinSentenceItems(communal)}`;
      sentence = sentence ? `${sentence}, ${communalText}` : communalText;
    }
    if (other.length) {
      sentence = sentence ? `${sentence} and ${this.joinSentenceItems(other)}` : this.joinSentenceItems(other);
    }
    return this.capitalizeSentence(sentence.trim());
  }

  private secondaryImprovementSentencePart(value: string): string {
    const parsed = this.parseSecondaryImprovementSelection(value);
    if (!parsed) return '';
    if (parsed.group === 'other') return parsed.option;
    if (parsed.group === 'communalArea') return `common area improvements include ${parsed.option}`;
    const suffixes: Record<string, string> = {
      retainingWalls: 'retaining walls',
      fencing: 'fencing',
      driveway: 'driveway',
      landscaping: 'landscaping',
    };
    return `${parsed.option} ${suffixes[parsed.group] || ''}`.trim();
  }

  private parseSecondaryImprovementSelection(value: string): { group: string; option: string } | null {
    const [group, ...optionParts] = value.split('|');
    const option = optionParts.join('|').trim();
    return group && option ? { group, option } : null;
  }

  openBuildingGroupOptions(building: Building, fields: BuildingFieldConfig[]): void {
    const optionFields = fields.filter((field) => field.optionList);
    if (!optionFields.length) return;
    forkJoin(optionFields.map((field) => this.options.get(field.optionList!))).subscribe((optionGroups) => {
      const groups = optionFields.map((field, index) => ({
        key: field.key,
        label: field.label,
        options: optionGroups[index],
        selected: this.quickSelectCurrentValue(building, field, optionGroups[index]),
        singleSelect: !!field.singleSelect,
      }));
      this.quickSelectModal = {
        open: true,
        title: this.buildingGroupQuickSelectTitle(fields),
        description: this.buildingGroupQuickSelectDescription(fields),
        buildingId: building.id,
        groups,
      };
    });
  }

  private quickSelectCurrentValue(building: Building, field: BuildingFieldConfig, options: string[]): string[] {
    const value = String(this.buildingFieldValue(building, field.key) ?? '');
    if (field.key === 'secondaryImprovements') {
      const groups = this.secondaryImprovementsOptionGroups(options);
      return splitCsv(this.secondaryImprovementsSelectionValue(value, groups));
    }
    return splitCsv(this.optionModalCurrentValue(value, field.key));
  }

  private buildingGroupQuickSelectTitle(fields: BuildingFieldConfig[]): string {
    if (fields === this.externalBuildingFields) return 'External Description Quick Select';
    return 'Internal Description Quick Select';
  }

  private buildingGroupQuickSelectDescription(fields: BuildingFieldConfig[]): string {
    if (fields === this.externalBuildingFields) return 'Quick select options for External Description fields';
    return 'Quick select options for Internal Description fields';
  }

  quickOptionSelected(group: QuickSelectGroup, option: string): boolean {
    return group.selected.includes(option);
  }

  toggleQuickOption(group: QuickSelectGroup, option: string): void {
    if (group.singleSelect) {
      group.selected = [option];
      return;
    }
    group.selected = group.selected.includes(option)
      ? group.selected.filter((item) => item !== option)
      : [...group.selected, option];
  }

  applyQuickSelect(): void {
    const building = this.buildings.find((item) => item.id === this.quickSelectModal.buildingId);
    if (!building) return;
    this.quickSelectModal.groups.forEach((group) => {
      const selectedValue = group.singleSelect ? (group.selected[0] || '') : joinCsv(group.selected, group.options);
      this.setBuildingField(building, group.key, group.key === 'secondaryImprovements' ? this.formatSecondaryImprovementsValue(selectedValue) : this.selectedOptionValue(group.key, selectedValue));
    });
    this.quickSelectModal.open = false;
  }

  closeQuickSelect(): void {
    this.quickSelectModal.open = false;
  }

  openYearPicker(building: Building, field: BuildingFieldKey, title: string): void {
    const currentValue = String(this.buildingFieldValue(building, field) ?? '');
    const parsedValue = this.parseYearDisplayValue(currentValue);
    const yearValue = Number(parsedValue.year);
    const decadeStart = parsedValue.year && Number.isFinite(yearValue) ? Math.floor(yearValue / 10) * 10 : null;
    const selectedDecade = decadeStart ? this.yearDecades.find((decade) => decade.startsWith(String(decadeStart))) || '' : '';
    this.yearPickerModal = {
      open: true,
      title,
      buildingId: building.id,
      field,
      selectedQualifier: parsedValue.qualifier,
      selectedDecade,
      selectedYear: parsedValue.year,
    };
  }

  toggleYearQualifier(qualifier: string): void {
    this.yearPickerModal.selectedQualifier = this.yearPickerModal.selectedQualifier === qualifier ? '' : qualifier;
  }

  toggleYearDecade(decade: string): void {
    if (this.yearPickerModal.selectedDecade === decade) {
      this.yearPickerModal.selectedDecade = '';
      this.yearPickerModal.selectedYear = '';
      return;
    }
    this.yearPickerModal.selectedDecade = decade;
    this.yearPickerModal.selectedYear = '';
  }

  toggleYear(year: number): void {
    const value = String(year);
    this.yearPickerModal.selectedYear = this.yearPickerModal.selectedYear === value ? '' : value;
  }

  applyYearPicker(): void {
    if (!this.yearPickerModal.selectedYear || !this.yearPickerModal.field) return;
    const building = this.buildings.find((item) => item.id === this.yearPickerModal.buildingId);
    if (building) this.setBuildingField(building, this.yearPickerModal.field, this.formatYearDisplayValue(this.yearPickerModal.selectedQualifier, this.yearPickerModal.selectedYear));
    this.closeYearPicker();
  }

  closeYearPicker(): void {
    this.yearPickerModal.open = false;
  }

  applyOption(value: string): void {
    if (!this.optionModal.field) return;
    if (this.optionModal.field.startsWith('building:')) {
      const [, buildingId, key] = this.optionModal.field.split(':');
      const building = this.buildings.find((item) => item.id === buildingId);
      if (building) this.setBuildingField(building, key as BuildingFieldKey, key === 'secondaryImprovements' ? this.formatSecondaryImprovementsValue(value) : this.selectedOptionValue(key, value));
      this.optionModal.open = false;
      return;
    }
    if (this.optionModal.field.startsWith('floorArea:')) {
      const [, buildingId, indexValue] = this.optionModal.field.split(':');
      const building = this.buildings.find((item) => item.id === buildingId);
      const floorArea = building?.floorAreas?.[Number(indexValue)];
      if (building && floorArea) this.setFloorAreaTitle(building, floorArea, value);
      this.optionModal.open = false;
      return;
    }
    if (this.optionModal.field.startsWith('otherBuilding:')) {
      const [, buildingId, key] = this.optionModal.field.split(':');
      const building = this.otherBuildings.find((item) => item.id === buildingId);
      if (building) this.setOtherBuildingField(building, key as OtherBuildingFieldKey, key === 'pcItems' ? this.formatPcItemsValue(value) : value);
      this.optionModal.open = false;
      return;
    }
    this.form.get(this.optionModal.field)?.setValue(this.selectedOptionValue(this.optionModal.field, value));
  }

  private optionModalHint(field: string): string {
    return field === 'internalLayout' ? this.internalLayoutSuffix : '';
  }

  private optionModalCurrentValue(valueOrPath: string, field: string, isFormPath = false): string {
    const value = isFormPath ? String(this.form.get(valueOrPath)?.value || '') : valueOrPath;
    return field === 'internalLayout' ? this.removeInternalLayoutSuffix(value) : value;
  }

  private selectedOptionValue(field: string, value: string): string {
    if (field !== 'internalLayout') return value;
    const selectedValue = this.removeInternalLayoutSuffix(value).trim();
    return selectedValue ? `${selectedValue} ${this.internalLayoutSuffix}` : '';
  }

  private removeInternalLayoutSuffix(value: string): string {
    const trimmedValue = value.trimEnd();
    const suffixStart = trimmedValue.length - this.internalLayoutSuffix.length;
    if (suffixStart <= 0 || trimmedValue.slice(suffixStart).toLowerCase() !== this.internalLayoutSuffix.toLowerCase()) return value;
    const previousCharacter = trimmedValue.charAt(suffixStart - 1);
    return previousCharacter.trim() === '' ? trimmedValue.slice(0, suffixStart).trimEnd() : value;
  }

  private formatPcItemsValue(value: string): string {
    const selectedValues = splitCsv(value);
    const conditionOptions = ['Excellent', 'Good', 'Average', 'Fair'];
    const condition = conditionOptions.find((option) => selectedValues.includes(option));
    const pcItems = selectedValues.filter((item) => !conditionOptions.includes(item));
    const itemsText = this.capitalizeSentence(this.joinSentenceItems(pcItems));
    if (itemsText && condition) return `${itemsText} in ${condition.toLowerCase()} condition`;
    if (itemsText) return itemsText;
    return condition ? `${condition} condition` : '';
  }

  private joinSentenceItems(values: string[]): string {
    if (values.length <= 2) return values.join(' and ');
    return `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}`;
  }

  private capitalizeSentence(value: string): string {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  }

  yearQualifierValue(building: Building, key: BuildingFieldKey): string {
    return this.parseYearDisplayValue(String(this.buildingFieldValue(building, key) ?? '')).qualifier;
  }

  yearValue(building: Building, key: BuildingFieldKey): string {
    return this.parseYearDisplayValue(String(this.buildingFieldValue(building, key) ?? '')).year;
  }

  setBuildingYearQualifier(building: Building, key: BuildingFieldKey, qualifier: string): void {
    const current = this.parseYearDisplayValue(String(this.buildingFieldValue(building, key) ?? ''));
    this.setBuildingField(building, key, this.formatYearDisplayValue(qualifier, current.year));
  }

  setBuildingYearValue(building: Building, key: BuildingFieldKey, year: string | number | null): void {
    const current = this.parseYearDisplayValue(String(this.buildingFieldValue(building, key) ?? ''));
    this.setBuildingField(building, key, this.formatYearDisplayValue(current.qualifier, String(year ?? '')));
  }

  private parseYearDisplayValue(value: string): { qualifier: string; year: string } {
    const trimmedValue = value.trim();
    const qualifier = this.yearQualifiers.find((option) => trimmedValue.toLowerCase().startsWith(option.toLowerCase()));
    if (!qualifier) return { qualifier: '', year: trimmedValue };
    return { qualifier, year: trimmedValue.slice(qualifier.length).trim() };
  }

  private formatYearDisplayValue(qualifier: string, year: string): string {
    const selectedQualifier = qualifier.trim();
    const selectedYear = year.trim();
    return [selectedQualifier, selectedYear].filter(Boolean).join(' ');
  }

  addBuilding(): void {
    if (!this.isMultiDwellingValuationType) return;
    const dwellingNumber = this.nextAvailableDwellingNumber();
    const building = emptyBuilding(dwellingNumber);
    building.name = `Dwelling ${dwellingNumber}`;
    building.isDuplicate = false;
    this.buildings = [...this.buildings, building];
    this.allBuildings = [...this.allBuildings, building];
    this.activeBuildingId = building.id;
    this.unsaved.set(true);
  }

  private ensureActiveBuildingVisible(): void {
    if (!this.activeBuildingId || !this.visibleBuildings.some((building) => building.id === this.activeBuildingId)) {
      this.activeBuildingId = this.visibleBuildings[0]?.id || null;
    }
  }

  private normalizeValuationTypeText(value: unknown): string {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private nextAvailableDwellingNumber(): number {
    const usedNumbers = new Set(
      this.allBuildings
        .filter((building) => !building.isDelete)
        .map((building) => building.name?.trim().match(/^Dwelling\s+(\d+)$/i)?.[1])
        .filter((value): value is string => !!value)
        .map((value) => Number(value))
    );
    let dwellingNumber = this.buildings.length + 1;
    while (usedNumbers.has(dwellingNumber)) dwellingNumber += 1;
    return dwellingNumber;
  }

  private dedupeDwellingsByName(buildings: Building[]): Building[] {
    const result: Building[] = [];
    const indexes = new Map<string, number>();
    buildings.forEach((building) => {
      const nameKey = building.name?.trim().toLowerCase();
      if (!nameKey) {
        result.push(building);
        return;
      }
      const existingIndex = indexes.get(nameKey);
      if (existingIndex === undefined) {
        indexes.set(nameKey, result.length);
        result.push(building);
        return;
      }
      if (result[existingIndex].isDelete && !building.isDelete) result[existingIndex] = building;
    });
    return result;
  }

  private dwellingNameExists(name: string, excludedId: string | null = null): boolean {
    const nameToCheck = name.trim().toLowerCase();
    if (!nameToCheck) return false;
    return this.allBuildings.some((building) =>
      building.id !== excludedId &&
      !building.isDelete &&
      !!building.name &&
      building.name.trim().toLowerCase() === nameToCheck
    );
  }
  
  hasDuplicateNames() {
    return this.allBuildings.some(b => b.isDuplicate);
  }

  removeBuilding(id: string): void {
    this.allBuildings.forEach(building => {
      if (building.id === id) {
        building.isDelete = true;
      }
    });
    this.buildings = this.allBuildings.filter((building) => building.isDelete === false);
    if (this.activeBuildingId === id) this.activeBuildingId = this.visibleBuildings[0]?.id || null;
    this.ensureActiveBuildingVisible();
    this.unsaved.set(true);
  }

  requestRemoveBuilding(building: Building): void {
    this.deleteDwellingPrompt = {
      open: true,
      buildingId: building.id,
      buildingName: building.name || 'this dwelling',
    };
  }

  cancelRemoveBuilding(): void {
    this.deleteDwellingPrompt = { open: false, buildingId: '', buildingName: '' };
  }

  confirmRemoveBuilding(): void {
    const buildingId = this.deleteDwellingPrompt.buildingId;
    this.cancelRemoveBuilding();
    if (buildingId) this.removeBuilding(buildingId);
  }

  toggleBuilding(id: string): void {
    this.activeBuildingId = this.activeBuildingId === id ? null : id;
  }

  buildingFieldValue(building: Building, key: BuildingFieldKey): string | number | null {
    return building[key] as string | number | null;
  }

  setBuildingField(building: Building, key: BuildingFieldKey, value: string | number | null): void {
    const current = building[key];
    const nextValue = typeof current === 'number' || current === null
      ? this.normalizeBuildingNumber(key, value)
      : String(value ?? '');
    (building as unknown as Record<string, unknown>)[key] = nextValue;
    this.unsaved.set(true);
  }

  addFloorArea(building: Building): void {
    building.floorAreas = [
      ...(building.floorAreas || []),
      {
        id: `floor-area-${Date.now()}-${(building.floorAreas || []).length + 1}`,
        title: '',
        grossArea: null,
      },
    ];
    this.unsaved.set(true);
  }

  removeFloorArea(building: Building, index: number): void {
    building.floorAreas = (building.floorAreas || []).filter((_, itemIndex) => itemIndex !== index);
    this.unsaved.set(true);
  }

  setFloorAreaTitle(building: Building, floorArea: FloorArea, value: string): void {
    floorArea.title = value || '';
    this.normalizeBuildingFloorAreas(building);
    this.unsaved.set(true);
  }

  setFloorAreaGrossArea(building: Building, floorArea: FloorArea, value: string | number | null): void {
    floorArea.grossArea = this.normalizeAreaNumber(value);
    this.normalizeBuildingFloorAreas(building);
    this.unsaved.set(true);
  }

  openFloorAreaTitleOptions(building: Building, index: number): void {
    const floorArea = (building.floorAreas || [])[index];
    this.optionModal = {
      open: true,
      title: 'Floor Area',
      field: `floorArea:${building.id}:${index}`,
      options: this.floorAreaTitleOptions,
      optionGroups: [],
      currentValue: floorArea?.title || '',
      singleSelect: true,
      hint: '',
    };
  }

  totalFloorAreas(building: Building): number {
    return [
      building.livingAreasSqm,
      building.outdoorAreasSqm,
      building.carAreasSqm,
      building.otherAreasSqm,
      ...(building.floorAreas || []).map((floorArea) => floorArea.grossArea),
    ].reduce<number>((total, value) => total + (Number(value) || 0), 0);
  }

  private normalizeBuilding(building: Building): Building {
    const normalized = { ...emptyBuilding(0), ...building };
    normalized.floorAreas = this.normalizeFloorAreas(building.floorAreas);
    return normalized;
  }

  private normalizeBuildingFloorAreas(building: Building): void {
    building.floorAreas = this.normalizeFloorAreas(building.floorAreas);
  }

  private normalizeFloorAreas(floorAreas: FloorArea[] | null | undefined): FloorArea[] {
    if (!Array.isArray(floorAreas)) return [];
    return floorAreas.map((floorArea, index) => ({
      id: floorArea.id ?? floorArea.floorAreasId ?? `floor-area-${Date.now()}-${index + 1}`,
      floorAreasId: floorArea.floorAreasId ?? null,
      floorAreasNo: index + 1,
      title: String(floorArea.title ?? '').trim(),
      grossArea: this.normalizeAreaNumber(floorArea.grossArea),
    }));
  }

  private normalizeAreaNumber(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? Math.max(0, numberValue) : null;
  }

  private normalizeBuildingNumber(key: BuildingFieldKey, value: string | number | null): number | null | string {
    const numericKeys: BuildingFieldKey[] = ['bedrooms', 'bathrooms', 'livingAreas', 'livingAreasSqm', 'outdoorAreasSqm', 'otherAreasSqm', 'carAreasSqm', 'undercoverCarSpaces', 'carSpaces'];
    if (!numericKeys.includes(key)) return String(value ?? '');
    if (value === null || value === '') return null;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? Math.max(0, numberValue) : null;
  }

  addOtherBuilding(): void {
    const building = emptyOtherBuilding(this.otherBuildings.length + 1);
    this.otherBuildings = [...this.otherBuildings, building];
    this.activeOtherBuildingId = building.id;
    this.unsaved.set(true);
  }

  removeOtherBuilding(id: string): void {
    this.otherBuildings = this.otherBuildings.filter((building) => building.id !== id);
    if (this.activeOtherBuildingId === id) this.activeOtherBuildingId = this.otherBuildings[0]?.id || null;
    this.unsaved.set(true);
  }

  toggleOtherBuilding(id: string): void {
    this.activeOtherBuildingId = this.activeOtherBuildingId === id ? null : id;
  }

  setOtherBuildingField(building: OtherBuilding, key: OtherBuildingFieldKey, value: string | number | null): void {
    if (key === 'id') return;
    const nextValue = key === 'area'
      ? this.sanitizeOtherBuildingArea(value)
      : key === 'notes'
        ? String(value ?? '').slice(0, 300)
        : String(value ?? '');
    (building as unknown as Record<string, unknown>)[key] = nextValue;
    this.unsaved.set(true);
  }

  onOtherBuildingAreaInput(building: OtherBuilding, event: Event): void {
    const input = event.target as HTMLInputElement;
    const nextValue = this.sanitizeOtherBuildingArea(input.value);
    if (input.value !== nextValue) input.value = nextValue;
    building.area = nextValue;
    this.unsaved.set(true);
  }

  formatOtherBuildingArea(building: OtherBuilding): void {
    building.area = this.normalizeOtherBuildingArea(building.area);
    this.unsaved.set(true);
  }

  private normalizeOtherBuildingArea(value: string | number | null): string {
    const sanitizedValue = this.sanitizeOtherBuildingArea(value);
    if (sanitizedValue === '') return '0.00';
    const numberValue = Number(sanitizedValue);
    if (!Number.isFinite(numberValue)) return '0.00';
    const positiveValue = Math.max(0, numberValue);
    return positiveValue.toFixed(2);
  }

  private sanitizeOtherBuildingArea(value: string | number | null): string {
    const rawValue = String(value ?? '').replace(/[^\d.]/g, '');
    const hasDecimal = rawValue.includes('.');
    const [wholeValue, ...decimalValues] = rawValue.split('.');
    const maxLength = 10;
    const decimalPlaces = 2;
    const maxWholeDigits = maxLength - decimalPlaces - 1;
    const wholeDigits = wholeValue.slice(0, maxWholeDigits);
    if (!hasDecimal) return wholeDigits;
    const decimalDigits = decimalValues.join('').slice(0, decimalPlaces);
    return `${wholeDigits || '0'}.${decimalDigits}`;
  }

  openCameraPopout(initialImageName: string | null = null, allowBlankImageName = false): void {
    this.cameraInitialImageName = initialImageName;
    this.cameraAllowBlankImageName = allowBlankImageName;
    this.showCameraModal = true;
  }

  closeCameraModal(): void {
    this.showCameraModal = false;
    this.cameraInitialImageName = null;
    this.cameraAllowBlankImageName = false;
  }

  startDrawing(event: PointerEvent): void {
    if (this.notesMode !== 'drawing') return;
    const point = this.canvasPoint(event);
    this.drawing = true;
    this.lastPoint = point;
  }

  draw(event: PointerEvent): void {
    if (!this.drawing || this.notesMode !== 'drawing') return;
    const canvas = this.drawingCanvas?.nativeElement;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    const point = this.canvasPoint(event);
    context.strokeStyle = '#003f2d';
    context.lineWidth = 3;
    context.lineCap = 'round';
    context.beginPath();
    context.moveTo(this.lastPoint.x, this.lastPoint.y);
    context.lineTo(point.x, point.y);
    context.stroke();
    this.lastPoint = point;
    this.form.controls.drawingData.setValue(canvas.toDataURL('image/png'));
  }

  stopDrawing(): void {
    const canvas = this.drawingCanvas?.nativeElement;
    if (this.drawing && canvas) this.form.controls.drawingData.setValue(canvas.toDataURL('image/png'));
    this.drawing = false;
  }

  clearDrawing(): void {
    const canvas = this.drawingCanvas?.nativeElement;
    const context = canvas?.getContext('2d');
    if (canvas && context) context.clearRect(0, 0, canvas.width, canvas.height);
    this.form.controls.drawingData.setValue('');
  }

  private setupCameraChannel(): void {
    if (!this.jobId) return;
    this.channel = this.cameraChannels.create(this.jobId);
    this.channel.subscribe((message) => {
      if (message.type === 'photo-saved') this.toast.success('Photo saved', 'The photo list has been updated.');
    });
    this.channel.post({ type: 'form-heartbeat' });
    this.heartbeatId = window.setInterval(() => this.channel?.post({ type: 'form-heartbeat' }), 1000);
    window.addEventListener('beforeunload', () => this.channel?.post({ type: 'form-closing' }));
  }

  private closeCameraContext(): void {
    if (this.heartbeatId) window.clearInterval(this.heartbeatId);
    this.channel?.post({ type: 'form-closing' });
    this.channel?.close();
  }

  private restoreDrawing(): void {
    const canvas = this.drawingCanvas?.nativeElement;
    const drawingData = this.form.controls.drawingData.value;
    const dataUrlPrefix = 'data:image/png;base64,';
    if (!canvas || typeof drawingData !== 'string' || !drawingData.startsWith(dataUrlPrefix)) return;
    const encodedImage = drawingData.slice(dataUrlPrefix.length).replaceAll(' ', '').replaceAll('\n', '').replaceAll('\r', '').replaceAll('\t', '');
    if (!encodedImage || encodedImage.length % 4 !== 0) return;
    const raw = atob(encodedImage);
    const bytes = new Uint8Array(raw.length);
    for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
    void createImageBitmap(new Blob([bytes], { type: 'image/png' }))
      .then((image) => {
        const context = canvas.getContext('2d');
        if (!context) return;
        context.clearRect(0, 0, canvas.width, canvas.height);
        const drawCanvasImage = context['drawImage'].bind(context);
        drawCanvasImage(image, 0, 0, canvas.width, canvas.height);
        image.close();
      }, () => undefined);
  }

  private restoreDrawingAfterRender(): void {
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.restoreDrawing()));
  }

  private canvasPoint(event: PointerEvent): { x: number; y: number } {
    const canvas = this.drawingCanvas!.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (event.clientX - rect.left) * scaleX, y: (event.clientY - rect.top) * scaleY };
  }

  private parseJsonArray<T>(value: unknown, fallback: T[]): T[] {
    if (!value || typeof value !== 'string') return fallback;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed as T[] : fallback;
    } catch {
      return fallback;
    }
  }

      
    checkDuplicateName(currentBuilding: any) {
      if (!currentBuilding.name) {
        currentBuilding.isDuplicate = false;
        return;
      }

      currentBuilding.isDuplicate = this.dwellingNameExists(currentBuilding.name, currentBuilding.id);
    }

}
